function build3DMapDebugSummaryHtml({
  imageData,
  debugCandidates = [],
  cameraSearchSummary = null,
  options = {},
  defaultWidth = 1536,
  defaultHeight = 1024,
}) {
  const coarseCandidates = debugCandidates.filter((candidate) => candidate.phase === "coarse");
  const refineCandidates = debugCandidates.filter((candidate) => candidate.phase === "refine");
  const selectedCandidate = [...debugCandidates].reverse().find((candidate) => candidate.phase === "selected") || null;
  const selectionGroups = groupDebugCandidatesBySelection(debugCandidates);
  const searchConfig = cameraSearchSummary?.searchConfig || null;
  const searchConfigRows = searchConfig ? [
    ["Preferred pitch", formatDebugNumber(searchConfig.preferredPitch, 1)],
    ["Search pitches", Array.isArray(searchConfig.cameraSearchPitches) ? searchConfig.cameraSearchPitches.map((pitch) => formatDebugNumber(pitch, 1)).join(", ") : "n/a"],
    ["Coarse bearings", Number.isFinite(searchConfig.coarseBearingCount) ? `${searchConfig.coarseBearingCount} (${formatDebugNumber(searchConfig.coarseBearingStepDegrees, 1)}deg step)` : "n/a"],
    ["Coarse card state", searchConfig.coarseCenterInitialBeforeScoring ? "Forced center-initial recenter before scoring and thumbnail capture" : "Direct fit state"],
    ["Refine offsets", Array.isArray(searchConfig.refineBearingOffsets) ? searchConfig.refineBearingOffsets.map((offset) => formatDebugNumber(offset, 1)).join(", ") : "n/a"],
    ["Retry limit", formatDebugInteger(searchConfig.robustRetryLimit)],
    ["Tighten zoom step", formatDebugNumber(searchConfig.tightenZoomStep, 2)],
    ["Tighten max steps", formatDebugInteger(searchConfig.tightenMaxSteps)],
  ] : [];
  const scoreWeightRows = [
    ["Coverage reward", "visibleSampleRatio x 3700 x coverageRewardMultiplier"],
    ["On-screen reward", "onScreenRatio x 1400"],
    ["Inside-padding reward", "insidePaddingRatio x 1100"],
    ["Area reward", "min(0.78, footprintRatio) x 4200 x footprintRewardMultiplier"],
    ["Center penalty", "centerDistanceNormalized x 950 (0 during coarse search)"],
    ["Margin penalty", "max(0, adaptivePadding - minMargin) x 30"],
    ["Out-of-bounds penalty", "100000 when outsidePaddingAreaRatio > 0"],
    ["Pitch penalty", "abs(candidatePitch - preferredPitch) x 8"],
  ];
  const metricExplainRows = [
    ["Visible", "Count of magenta route pixels found in the rendered canvas snapshot."],
    ["Area", "footprintRatio: route bounding-box area divided by full canvas area."],
    ["Visible samples / Total samples", "Ordered route samples used to measure route visibility along the path."],
    ["Visible sample ratio", "Visible sampled route length divided by total sampled route length."],
    ["Gaps", "Diagnostic only. Number of hidden internal runs between the first and last visible route sample."],
    ["Gap ratio", "Diagnostic only. Total hidden internal run length divided by the visible-span length."],
    ["Ratios W / H", "Route bounding-box width and height relative to the canvas size."],
    ["Outside pad area", "Fraction of route bounding-box area that falls outside the safe padded frame."],
    ["Center offset", "Normalized distance between route center and canvas center."],
  ];

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>3D Camera Search Debug Summary</title>
  <style>
    :root {
      --bg: #f3f6fb;
      --panel: #ffffff;
      --panel-border: rgba(15, 23, 42, 0.08);
      --text: #111827;
      --muted: #4b5563;
      --subtle: #6b7280;
      --accent: #1d4ed8;
      --good: #166534;
      --danger: #b91c1c;
      color-scheme: light;
      font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      background: linear-gradient(180deg, #f5f8fd 0%, #eef3fb 100%);
      color: var(--text);
    }
    .page {
      max-width: 1440px;
      margin: 0 auto;
      padding: 28px 20px 40px;
      display: grid;
      gap: 20px;
    }
    .panel {
      background: var(--panel);
      border: 1px solid var(--panel-border);
      border-radius: 20px;
      box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
      overflow: hidden;
    }
    .hero {
      display: grid;
      grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr);
      align-items: stretch;
    }
    .hero-image-wrap {
      background: #edf2f7;
      border-right: 1px solid var(--panel-border);
      padding: 22px;
    }
    .hero-image {
      width: 100%;
      display: block;
      border-radius: 14px;
      border: 1px solid rgba(17, 24, 39, 0.08);
      background: #fff;
    }
    .hero-meta {
      padding: 22px;
      display: grid;
      gap: 20px;
      align-content: start;
    }
    .eyebrow {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--subtle);
    }
    h1, h2, h3, p {
      margin: 0;
    }
    h1 {
      font-size: 28px;
      line-height: 1.1;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }
    .summary-item {
      border: 1px solid var(--panel-border);
      border-radius: 12px;
      padding: 10px 12px;
      background: #fbfcff;
    }
    .summary-label {
      font-size: 12px;
      color: var(--subtle);
      margin-bottom: 4px;
    }
    .summary-value {
      font-size: 15px;
      font-weight: 700;
    }
    .config-list {
      display: grid;
      gap: 8px;
    }
    .config-row {
      display: grid;
      grid-template-columns: 180px minmax(0, 1fr);
      gap: 12px;
      font-size: 13px;
      line-height: 1.4;
    }
    .config-key {
      color: var(--subtle);
      font-weight: 600;
    }
    .config-value {
      font-weight: 600;
      color: var(--text);
      word-break: break-word;
    }
    .section {
      padding: 22px;
      display: grid;
      gap: 16px;
    }
    .explain-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
    }
    .explain-card {
      border: 1px solid var(--panel-border);
      border-radius: 16px;
      background: #fbfcff;
      padding: 16px;
      display: grid;
      gap: 12px;
    }
    .explain-title {
      font-size: 16px;
      font-weight: 800;
    }
    .section-header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 16px;
    }
    .section-title {
      font-size: 22px;
      font-weight: 800;
    }
    .section-subtitle {
      color: var(--muted);
      font-size: 13px;
    }
    .card-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(265px, 1fr));
      gap: 14px;
    }
    .candidate-card {
      border: 1px solid var(--panel-border);
      border-radius: 14px;
      overflow: hidden;
      background: #fff;
      display: grid;
      grid-template-rows: auto 1fr;
    }
    .candidate-image {
      width: 100%;
      display: block;
      background: #eef1f6;
      border-bottom: 1px solid var(--panel-border);
    }
    .candidate-image--empty {
      min-height: 150px;
      display: grid;
      place-items: center;
      color: var(--muted);
      font-size: 13px;
    }
    .candidate-body {
      padding: 12px 14px 14px;
      display: grid;
      gap: 6px;
      font-size: 13px;
      line-height: 1.35;
    }
    .candidate-phase {
      font-size: 13px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--accent);
    }
    .candidate-line strong {
      font-weight: 800;
    }
    .candidate-line--decision {
      font-weight: 700;
    }
    .candidate-line--decision.accepted {
      color: var(--good);
    }
    .candidate-line--decision.selected {
      color: var(--good);
    }
    .candidate-line--decision.retry-next-candidate,
    .candidate-line--decision.reverted {
      color: var(--danger);
    }
    .selection-group {
      display: grid;
      gap: 12px;
      padding: 18px;
      border: 1px solid var(--panel-border);
      border-radius: 16px;
      background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
    }
    .selection-group-header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 16px;
    }
    .selection-group-title {
      font-size: 18px;
      font-weight: 800;
    }
    .selection-group-subtitle {
      color: var(--muted);
      font-size: 13px;
    }
    .selection-cards {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(265px, 1fr));
      gap: 14px;
    }
    .empty-state {
      padding: 28px;
      text-align: center;
      color: var(--muted);
      font-size: 14px;
      border: 1px dashed var(--panel-border);
      border-radius: 14px;
      background: #fbfcff;
    }
    @media (max-width: 1100px) {
      .hero {
        grid-template-columns: 1fr;
      }
      .hero-image-wrap {
        border-right: 0;
        border-bottom: 1px solid var(--panel-border);
      }
      .summary-grid {
        grid-template-columns: 1fr;
      }
      .config-row {
        grid-template-columns: 1fr;
        gap: 4px;
      }
      .explain-grid {
        grid-template-columns: 1fr;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <section class="panel hero">
      <div class="hero-image-wrap">
        <img class="hero-image" src="${imageData}" alt="Final rendered map">
      </div>
      <div class="hero-meta">
        <div>
          <div class="eyebrow">Mythic Maps</div>
          <h1>3D Camera Search Debug Summary</h1>
        </div>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">Coarse candidates</div>
            <div class="summary-value">${escapeHtml(String(coarseCandidates.length))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Refine candidates</div>
            <div class="summary-value">${escapeHtml(String(refineCandidates.length))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Selection tries</div>
            <div class="summary-value">${escapeHtml(String(selectionGroups.length))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Rendered size</div>
            <div class="summary-value">${escapeHtml(`${options.width || defaultWidth} x ${options.height || defaultHeight}`)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Final score</div>
            <div class="summary-value">${escapeHtml(formatDebugInteger(cameraSearchSummary?.selectedScore))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Final visible sample ratio</div>
            <div class="summary-value">${escapeHtml(formatDebugNumber(cameraSearchSummary?.visibleSampleRatio, 2))}</div>
          </div>
        </div>
        ${searchConfigRows.length ? `
        <div class="config-list">
          ${searchConfigRows.map(([key, value]) => `
          <div class="config-row">
            <div class="config-key">${escapeHtml(key)}</div>
            <div class="config-value">${escapeHtml(value)}</div>
          </div>`).join("")}
        </div>` : `<div class="section-subtitle">Camera search config unavailable.</div>`}
      </div>
    </section>

    <section class="panel section">
      <div class="section-header">
        <div class="section-title">How The Score Works</div>
        <div class="section-subtitle">These are the score components used by the current browser scorer. The old longest-run continuity ratio is no longer used.</div>
      </div>
      <div class="explain-grid">
        <div class="explain-card">
          <div class="explain-title">Score Formula</div>
          <div class="config-list">
            ${scoreWeightRows.map(([key, value]) => `
            <div class="config-row">
              <div class="config-key">${escapeHtml(key)}</div>
              <div class="config-value">${escapeHtml(value)}</div>
            </div>`).join("")}
          </div>
        </div>
        <div class="explain-card">
          <div class="explain-title">Metric Definitions</div>
          <div class="config-list">
            ${metricExplainRows.map(([key, value]) => `
            <div class="config-row">
              <div class="config-key">${escapeHtml(key)}</div>
              <div class="config-value">${escapeHtml(value)}</div>
            </div>`).join("")}
          </div>
        </div>
      </div>
    </section>

    <section class="panel section">
      <div class="section-header">
        <div class="section-title">Coarse Search</div>
        <div class="section-subtitle">${escapeHtml(`${coarseCandidates.length} candidates`)}</div>
      </div>
      ${coarseCandidates.length ? `<div class="card-grid">${coarseCandidates.map((candidate) => buildDebugCandidateCardHtml(candidate)).join("")}</div>` : `<div class="empty-state">No coarse candidates captured.</div>`}
    </section>

    ${refineCandidates.length ? `
    <section class="panel section">
      <div class="section-header">
        <div class="section-title">Refine Search</div>
        <div class="section-subtitle">${escapeHtml(`${refineCandidates.length} candidates`)}</div>
      </div>
      <div class="card-grid">${refineCandidates.map((candidate) => buildDebugCandidateCardHtml(candidate)).join("")}</div>
    </section>` : ""}

    <section class="panel section">
      <div class="section-header">
        <div class="section-title">Selection And Tightening</div>
        <div class="section-subtitle">${escapeHtml(`${selectionGroups.length} tries`)}</div>
      </div>
      ${selectionGroups.length ? selectionGroups.map((group) => buildSelectionGroupHtml(group)).join("") : `<div class="empty-state">No selection attempts captured.</div>`}
    </section>

    <section class="panel section">
      <div class="section-header">
        <div class="section-title">Final Selected Camera</div>
        <div class="section-subtitle">${selectedCandidate ? "Last emitted selected card" : "No selected card captured"}</div>
      </div>
      ${selectedCandidate ? `<div class="card-grid">${buildDebugCandidateCardHtml(selectedCandidate)}</div>` : `<div class="empty-state">No selected candidate captured.</div>`}
    </section>
  </div>
</body>
</html>`;
}

function groupDebugCandidatesBySelection(debugCandidates) {
  const selectionGroups = [];
  const groupsByAttempt = new Map();
  const selectionPhases = new Set([
    "selection-start",
    "tighten-zoom",
    "tighten-center",
    "tighten",
    "tighten-revert",
    "selection",
  ]);

  debugCandidates.forEach((candidate) => {
    if (!Number.isFinite(candidate.selectionAttempt)) return;
    if (!selectionPhases.has(candidate.phase)) return;

    const attemptKey = String(candidate.selectionAttempt);
    let group = groupsByAttempt.get(attemptKey);
    if (!group) {
      group = {
        selectionAttempt: candidate.selectionAttempt,
        selectionTotal: candidate.selectionTotal,
        cards: [],
      };
      groupsByAttempt.set(attemptKey, group);
      selectionGroups.push(group);
    }

    group.cards.push(candidate);
    if (!Number.isFinite(group.selectionTotal) && Number.isFinite(candidate.selectionTotal)) {
      group.selectionTotal = candidate.selectionTotal;
    }
  });

  selectionGroups.forEach((group) => {
    group.cards.sort((leftCandidate, rightCandidate) => {
      const leftSort = getSelectionPhaseSortKey(leftCandidate);
      const rightSort = getSelectionPhaseSortKey(rightCandidate);
      if (leftSort !== rightSort) return leftSort - rightSort;
      return (leftCandidate.index || 0) - (rightCandidate.index || 0);
    });
  });

  return selectionGroups.sort((leftGroup, rightGroup) => leftGroup.selectionAttempt - rightGroup.selectionAttempt);
}

function buildSelectionGroupHtml(group) {
  const selectionCard = group.cards.find((candidate) => candidate.phase === "selection") || null;
  const sourceCandidate = selectionCard || group.cards.find((candidate) => Boolean(candidate.sourcePhase)) || null;
  const sourcePhase = sourceCandidate?.sourcePhase ? `Source: ${sourceCandidate.sourcePhase}` : "Selection retry flow";
  return `
    <div class="selection-group">
      <div class="selection-group-header">
        <div class="selection-group-title">Try ${escapeHtml(formatDebugInteger(group.selectionAttempt))}${Number.isFinite(group.selectionTotal) ? `/${escapeHtml(formatDebugInteger(group.selectionTotal))}` : ""}</div>
        <div class="selection-group-subtitle">${escapeHtml(sourcePhase)}</div>
      </div>
      <div class="selection-cards">
        ${group.cards.map((candidate) => buildDebugCandidateCardHtml(candidate)).join("")}
      </div>
    </div>`;
}

function buildDebugCandidateCardHtml(candidate) {
  const decisionClass = candidate.decision ? ` candidate-line--decision ${escapeHtml(candidate.decision)}` : "";
  const decisionText = candidate.decision
    ? `Decision: ${candidate.decision}${candidate.decisionReason ? ` (${candidate.decisionReason})` : ""}`
    : "";

  return `
    <article class="candidate-card">
      ${candidate.thumbnailDataUrl
        ? `<img class="candidate-image" src="${candidate.thumbnailDataUrl}" alt="${escapeHtml(candidate.phase || "candidate")} preview">`
        : `<div class="candidate-image candidate-image--empty">No preview</div>`}
      <div class="candidate-body">
        <div class="candidate-phase">${escapeHtml(getCandidatePhaseLabel(candidate))}</div>
        ${Number.isFinite(candidate.selectionAttempt) && Number.isFinite(candidate.selectionTotal)
          ? `<div class="candidate-line">Try: ${escapeHtml(formatDebugInteger(candidate.selectionAttempt))}/${escapeHtml(formatDebugInteger(candidate.selectionTotal))}</div>`
          : ""}
        ${candidate.sourcePhase ? `<div class="candidate-line">Source: ${escapeHtml(candidate.sourcePhase)}</div>` : ""}
        <div class="candidate-line">Score: ${escapeHtml(formatDebugInteger(candidate.score))}</div>
        <div class="candidate-line">Requested: ${escapeHtml(formatDebugNumber(candidate.bearing, 1))} / ${escapeHtml(formatDebugNumber(candidate.pitch, 1))}</div>
        <div class="candidate-line">Rendered: ${escapeHtml(formatDebugNumber(candidate.camera?.bearing, 1))} / ${escapeHtml(formatDebugNumber(candidate.camera?.pitch, 1))}</div>
        <div class="candidate-line">Forced preview: ${candidate.cameraForBoundsRejected ? "yes" : "no"}</div>
        <div class="candidate-line">Requested zoom: ${escapeHtml(formatDebugZoom(candidate.requestedZoom))}</div>
        <div class="candidate-line">Rendered zoom: ${escapeHtml(formatDebugZoom(candidate.camera?.zoom))}</div>
        ${Number.isFinite(candidate.acceptedTightenSteps) ? `<div class="candidate-line">Accepted tighten steps: ${escapeHtml(formatDebugInteger(candidate.acceptedTightenSteps))}</div>` : ""}
        ${decisionText ? `<div class="candidate-line${decisionClass}">${escapeHtml(decisionText)}</div>` : ""}
        <div class="candidate-line">Visible: ${escapeHtml(formatDebugInteger(candidate.visiblePixels))} / Area: ${escapeHtml(formatDebugNumber(candidate.footprintRatio, 2))}</div>
        <div class="candidate-line"><strong>Visible sample ratio: ${escapeHtml(formatDebugNumber(candidate.visibleSampleRatio, 2))}</strong></div>
        <div class="candidate-line">Visible samples: ${escapeHtml(formatDebugInteger(candidate.visibleSampleCount))} / Total samples: ${escapeHtml(formatDebugInteger(candidate.totalSampleCount))}</div>
        <div class="candidate-line">Gaps: ${escapeHtml(formatDebugInteger(candidate.internalGapCount))} / Gap ratio: ${escapeHtml(formatDebugNumber(candidate.internalGapRatio, 2))}</div>
        <div class="candidate-line">Ratios: W ${escapeHtml(formatDebugNumber(candidate.bboxWidthRatio, 2))} / H ${escapeHtml(formatDebugNumber(candidate.bboxHeightRatio, 2))}</div>
        <div class="candidate-line">Outside pad area: ${escapeHtml(formatDebugNumber(candidate.outsidePaddingAreaRatio, 2))}</div>
        <div class="candidate-line">Center offset: ${escapeHtml(formatDebugNumber(candidate.centerDistanceNormalized, 3))}</div>
        <div class="candidate-line">Coverage reward: ${escapeHtml(formatDebugInteger(candidate.coverageReward))}</div>
        <div class="candidate-line">Area reward: ${escapeHtml(formatDebugInteger(candidate.footprintReward))}</div>
      </div>
    </article>`;
}

function getCandidatePhaseLabel(candidate) {
  const phase = candidate?.phase || "candidate";
  const labelMap = {
    "coarse": "coarse (post-center)",
    "selection-start": "selection start",
    "tighten-zoom": "tighten zoom",
    "tighten-center": "tighten center",
    "tighten-revert": "tighten revert",
  };
  const label = labelMap[phase] || phase;
  if (Number.isFinite(candidate?.total) && candidate.total > 1 && Number.isFinite(candidate?.index)) {
    return `${label} ${candidate.index}/${candidate.total}`;
  }
  return label;
}

function getSelectionPhaseSortKey(candidate) {
  const phase = candidate?.phase || "";
  const stepIndex = Number.isFinite(candidate?.index) ? candidate.index : 0;

  if (phase === "selection-start") return 0;
  if (phase === "tighten-zoom") return 10 + (stepIndex * 4);
  if (phase === "tighten-center") return 11 + (stepIndex * 4);
  if (phase === "tighten") return 12 + (stepIndex * 4);
  if (phase === "tighten-revert") return 13 + (stepIndex * 4);
  if (phase === "selection") return 100;
  return 999;
}

function formatDebugInteger(value) {
  return Number.isFinite(value) ? String(Math.round(value)) : "n/a";
}

function formatDebugNumber(value, digits = 2) {
  return Number.isFinite(value) ? Number(value).toFixed(digits) : "n/a";
}

function formatDebugZoom(value) {
  return Number.isFinite(value) ? Number(value).toFixed(2) : "n/a";
}

function escapeHtml(text) {
  return String(text)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

module.exports = {
  build3DMapDebugSummaryHtml,
};
